import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { openWhatsApp } from "@/lib/whatsapp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { conIVA, fechaMX } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { Wallet, AlertTriangle, Users, Clock, MessageCircle, FileSpreadsheet } from "lucide-react";
import EstadoCuentaResumen from "@/components/EstadoCuentaResumen";
import ReporteModulo from "@/components/ReporteModulo";
import FiltroSelect, { labelDe } from "@/components/FiltroSelect";
import { useVendedoresActivos } from "@/hooks/useVendedoresActivos";

const fmt = (v) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
const noPagado = (p) => !(p.estatus_pago === "Cobrado" || p.fecha_pago || p.estatus === "Pago" || p.estatus === "Pedido cerrado");
const facturado = (p) => !!(p.factura || p.fecha_facturacion || p.estatus_pago === "Pendiente de cobro" || p.estatus === "Facturación" || p.estatus === "Vencimiento" || p.estatus === "Facturado");
const diasVenc = (p) => { if (!p.fecha_vencimiento) return null; const h = new Date(); h.setHours(0, 0, 0, 0); return Math.round((new Date(p.fecha_vencimiento + "T00:00:00") - h) / 864e5); };
const labelDias = (d) => (d == null ? "sin vencimiento" : d < 0 ? `${-d}d vencida` : d === 0 ? "vence hoy" : `vence en ${d}d`);
const cobVend = (x) => x.vendedor;
const TONO = { red: { border: "border-l-red-500", text: "text-red-600" }, amber: { border: "border-l-amber-500", text: "text-amber-600" }, blue: { border: "border-l-blue-600", text: "text-blue-600" } };

export default function Cobranza() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cob, setCob] = useState({ vencidas: [], porVencer: [], porCobrar: [], porCliente: [], porVendedor: [], recordatorios: [], recordatoriosVencidas: [] });
  const [diasAviso, setDiasAviso] = useState(5);
  const [resumen, setResumen] = useState(null);
  const [vendSel, setVendSel] = useState("todos");
  const vendedores = useVendedoresActivos(isAdmin, cob.porCobrar, cobVend);
  const fv = (arr) => (vendSel === "todos" ? arr : arr.filter((x) => x.vendedor === vendSel));

  const load = async () => {
      try {
        const [pRes, paramRes, cliRes] = await Promise.all([
          api.get("/pedidos"), api.get("/parametros"), api.get("/clientes"),
        ]);
        const diasA = Number(paramRes?.data?.dias_aviso_vencimiento) || 5;
        const clientesById = {}; (cliRes.data || []).forEach((c) => { clientesById[c.id] = c; });
        setDiasAviso(diasA);
        const pData = pRes.data;

        const cobItems = (pData || []).filter((p) => noPagado(p) && facturado(p)).map((p) => ({
          id: p.id, numero_pedido: p.numero_pedido, cliente: p.cliente, cliente_id: p.cliente_id, vendedor: p.vendedor?.full_name || "Sin asignar",
          factura: p.factura || "", fecha_facturacion: p.fecha_facturacion || null,
          fecha_vencimiento: p.fecha_vencimiento || null, dias: diasVenc(p), monto: conIVA(p.subtotal),
        }));
        const porCobrar = [...cobItems].sort((a, b) => (a.fecha_vencimiento || "9999").localeCompare(b.fecha_vencimiento || "9999"));
        const vencidas = porCobrar.filter((x) => x.dias != null && x.dias < 0);
        const porVencer = porCobrar.filter((x) => x.dias != null && x.dias >= 0 && x.dias <= diasA);
        const agrupar = (campo) => {
          const m = {};
          vencidas.forEach((x) => { const k = x[campo] || "—"; (m[k] = m[k] || { nombre: k, count: 0, monto: 0 }); m[k].count++; m[k].monto += x.monto || 0; });
          return Object.values(m).sort((a, b) => b.monto - a.monto);
        };
        const recordatorios = porVencer
          .map((x) => { const cl = clientesById[x.cliente_id] || {}; return { ...x, phone: cl.whatsapp || cl.telefono || "", activo: cl.recordatorio_activado !== false }; })
          .filter((x) => x.activo && x.phone);
        const recordatoriosVencidas = vencidas
          .map((x) => { const cl = clientesById[x.cliente_id] || {}; return { ...x, phone: cl.whatsapp || cl.telefono || "", activo: cl.recordatorio_activado !== false }; })
          .filter((x) => x.activo && x.phone);
        setCob({ vencidas, porVencer, porCobrar, porCliente: agrupar("cliente"), porVendedor: agrupar("vendedor"), recordatorios, recordatoriosVencidas });
      } finally {
        setLoading(false);
      }
  };
  useEffect(() => { load(); }, []);
  useRefetchOnFocus(load);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-8" data-testid="cobranza-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Cobranza</h1>
          <p className="text-slate-500 mt-2">Facturas vencidas, por vencer y recordatorios de pago.</p>
        </div>
        {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
        <FiltroSelect value={vendSel} onChange={setVendSel} todosLabel="Todos los vendedores" options={vendedores} testid="cobranza-filtro-vendedor" />
        <ReporteModulo
          titulo="REPORTE DE COBRANZA"
          filenameBase="Reporte cobranza"
          prefix="rep-cob"
          triggerTestId="cobranza-reporte-btn"
          buttonVariant="outline"
          rows={fv(cob.porCobrar)}
          getFecha={(x) => x.fecha_vencimiento || null}
          groupBy={(x) => x.vendedor || "Sin asignar"}
          filtros={[{ label: "Vendedor", value: labelDe(vendSel, vendedores) }]}
          columns={[
            { header: "Pedido", value: (x) => x.numero_pedido, mono: true },
            { header: "Cliente", value: (x) => x.cliente },
            { header: "Vendedor", value: (x) => x.vendedor || "-" },
            { header: "Factura", value: (x) => x.factura || "-" },
            { header: "Vencimiento", value: (x) => fechaMX(x.fecha_vencimiento) },
            { header: "Días", value: (x) => labelDias(x.dias) },
            { header: "Monto", align: "right", value: (x) => fmt(x.monto) },
          ]}
          subtotal={(rows) => fmt(rows.reduce((s, x) => s + Number(x.monto || 0), 0))}
          totales={(rows) => [
            { label: "Total por cobrar", value: fmt(rows.reduce((s, x) => s + Number(x.monto || 0), 0)) },
            { label: "Total vencido", value: fmt(rows.filter((x) => x.dias != null && x.dias < 0).reduce((s, x) => s + Number(x.monto || 0), 0)) },
          ]}
        />
        </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <div data-testid="cobranza-panel">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              { key: "vencidas", titulo: "Facturas vencidas", items: fv(cob.vencidas), tono: "red", Icon: AlertTriangle },
              { key: "porVencer", titulo: `Por vencer (${diasAviso} días)`, items: fv(cob.porVencer), tono: "amber", Icon: Clock },
              { key: "porCobrar", titulo: "Por cobrar (facturado)", items: fv(cob.porCobrar), tono: "blue", Icon: Wallet },
            ].map((b) => {
              const total = b.items.reduce((s, x) => s + (x.monto || 0), 0);
              const t = TONO[b.tono];
              return (
                <Card key={b.key} className={`border-l-4 ${t.border}`} data-testid={`cobranza-${b.key}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2"><b.Icon className={`h-4 w-4 ${t.text}`} /> {b.titulo}</span>
                      <span className={`font-bold ${t.text}`} data-testid={`cobranza-total-${b.key}`}>{fmt(total)}</span>
                    </CardTitle>
                    <p className="text-xs text-slate-500">{b.items.length} factura(s)</p>
                  </CardHeader>
                  <CardContent>
                    {b.items.length === 0 ? (
                      <div className="text-center py-6 text-slate-400 text-sm">Sin facturas</div>
                    ) : (
                      <div className="max-h-[260px] overflow-y-auto divide-y -mx-2">
                        {b.items.map((x) => (
                          <button key={x.id} onClick={() => navigate(`/pedidos?cliente=${encodeURIComponent(x.cliente)}`)} className="w-full text-left px-2 py-2 flex justify-between gap-3 hover:bg-slate-50 rounded" data-testid={`cobranza-item-${x.id}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{x.numero_pedido}</div>
                              <div className="text-xs text-slate-500 truncate">{x.cliente}</div>
                              <div className={`text-xs ${x.dias != null && x.dias < 0 ? "text-red-500" : "text-slate-400"}`}>{fechaMX(x.fecha_vencimiento, "—")} · {labelDias(x.dias)}</div>
                            </div>
                            <div className="text-sm font-semibold whitespace-nowrap">{fmt(x.monto)}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {cob.porCliente.length > 0 && (
              <Card data-testid="cobranza-por-cliente">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-red-600" /> Cartera vencida por cliente</CardTitle>
                  <p className="text-xs text-slate-500">Prioriza a quién cobrarle primero (mayor monto arriba).</p>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead className="text-right">Facturas</TableHead><TableHead className="text-right">Monto vencido</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {cob.porCliente.map((c, i) => (
                          <TableRow key={i} onClick={() => navigate(`/pedidos?cliente=${encodeURIComponent(c.nombre)}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`cobranza-cliente-row-${i}`}>
                            <TableCell className="font-medium">{c.nombre}</TableCell>
                            <TableCell className="text-right">{c.count}</TableCell>
                            <TableCell className="text-right font-semibold text-red-600">{fmt(c.monto)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
            {cob.porVendedor.length > 0 && (
              <Card data-testid="cobranza-por-vendedor">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-red-600" /> Cartera vencida por vendedor</CardTitle>
                  <p className="text-xs text-slate-500">Seguimiento por responsable (mayor monto arriba).</p>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>Vendedor</TableHead><TableHead className="text-right">Facturas</TableHead><TableHead className="text-right">Monto vencido</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {cob.porVendedor.map((c, i) => (
                          <TableRow key={i} data-testid={`cobranza-vendedor-row-${i}`}>
                            <TableCell className="font-medium">{c.nombre}</TableCell>
                            <TableCell className="text-right">{c.count}</TableCell>
                            <TableCell className="text-right font-semibold text-red-600">{fmt(c.monto)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          {isAdmin && cob.recordatorios.length > 0 && (
            <Card className="mt-4" data-testid="recordatorios-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><MessageCircle className="h-4 w-4 text-emerald-600" /> Recordatorios por WhatsApp (por vencer)</CardTitle>
                <p className="text-xs text-slate-500">Envía el recordatorio al cliente con un clic. Solo aparecen clientes con el recordatorio activado.</p>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {cob.recordatorios.map((x) => (
                    <div key={x.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{x.cliente}</div>
                        <div className="text-xs text-slate-500">{x.numero_pedido} · {fmt(x.monto)} · {fechaMX(x.fecha_vencimiento)} ({x.dias === 0 ? "vence hoy" : `en ${x.dias}d`})</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" variant="outline" className="gap-1" data-testid={`estado-cuenta-porVencer-${x.id}`}
                          onClick={() => setResumen({ tipo: "porVencer", items: cob.porVencer.filter((i) => i.cliente_id === x.cliente_id) })}>
                          <FileSpreadsheet className="h-4 w-4" /> Edo. cuenta
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1" data-testid={`recordatorio-wa-${x.id}`}
                          onClick={() => openWhatsApp(x.phone, `Estimado cliente ${x.cliente}, le recordamos que su factura del pedido ${x.numero_pedido} por ${fmt(x.monto)} vence el ${fechaMX(x.fecha_vencimiento)}. Agradecemos su pago puntual. — Cooper Slip`)}>
                          <MessageCircle className="h-4 w-4" /> WhatsApp
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {isAdmin && cob.recordatoriosVencidas.length > 0 && (
            <Card className="mt-4 border-l-4 border-l-red-500" data-testid="recordatorios-vencidas-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><MessageCircle className="h-4 w-4 text-red-600" /> Recordatorios por WhatsApp (vencidas)</CardTitle>
                <p className="text-xs text-slate-500">Facturas ya vencidas. Envía el recordatorio de pago al cliente con un clic. Solo aparecen clientes con el recordatorio activado.</p>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {cob.recordatoriosVencidas.map((x) => (
                    <div key={x.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{x.cliente}</div>
                        <div className="text-xs text-red-500">{x.numero_pedido} · {fmt(x.monto)} · venció el {fechaMX(x.fecha_vencimiento)} ({-x.dias}d vencida)</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" variant="outline" className="gap-1" data-testid={`estado-cuenta-vencidas-${x.id}`}
                          onClick={() => setResumen({ tipo: "vencidas", items: cob.vencidas.filter((i) => i.cliente_id === x.cliente_id) })}>
                          <FileSpreadsheet className="h-4 w-4" /> Edo. cuenta
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1" data-testid={`recordatorio-vencida-wa-${x.id}`}
                          onClick={() => openWhatsApp(x.phone, `Estimado cliente ${x.cliente}, le recordamos que su factura del pedido ${x.numero_pedido} por ${fmt(x.monto)} venció el ${fechaMX(x.fecha_vencimiento)} y se encuentra pendiente de pago. Le agradecemos regularizar su saldo a la brevedad. — Cooper Slip`)}>
                          <MessageCircle className="h-4 w-4" /> WhatsApp
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <EstadoCuentaResumen open={!!resumen} onClose={() => setResumen(null)} tipo={resumen?.tipo} items={resumen?.items || []} />
    </div>
  );
}
